import CampaignModal from './CampaignModal';
import { formatDate } from '../../utilities';
import { CampaignName } from '../../templates';
import { useAppContext } from '../../molecules';
import { Slug, Buttons } from '../../components';
import { campaignStatusesMap } from '../../templates';
import { useCampaignActions } from './useCampaignActions';
import { useCampaignModalOptions } from './useCampaignModalOptions';
import { useCampaigns, CampaignsListTemplate } from '../../templates';

const getColor = (status: string): SlugColorType => {
  switch (status) {
    case 'active':
      return 'main';
    case 'inactive':
      return 'danger';
    case 'completed':
      return 'success';
    default:
      return 'main';
  }
};

const deleteTooltip = "Can't delete campaign that has contacts attached";

const Campaigns = () => {
  const listResult = useCampaigns();
  const { refresh: refreshMeta } = useAppContext();
  const { initializing, agents, phones } = useCampaignModalOptions();
  const { refresh, reloading, initializing: fetching } = listResult;

  const {
    copyElement,
    viewElement,
    secretElement,
    transferElement,
    exportElement,
    importElement,
    element: actionElement,
    isOpened: isOpenedCampaignModal,
    updating,
    copying,
    transfering,
    initialValues,

    handleAdd,
    handleStartAdd,
    handleClose,
    handleStartDelete,
    handleEdit,
    handleStartEdit,
    handleView,
    handleStartCopy,
    handleStartSecrets,
    handleStartTransfer,
    handleStartExport,
    handleStartImport,
  } = useCampaignActions(() => {
    refresh();
    refreshMeta();
  });

  const loading =
    fetching || updating || copying || transfering || initializing;

  return (
    <>
      {viewElement}
      {copyElement}
      {secretElement}
      {actionElement}
      {transferElement}
      {exportElement}
      {importElement}
      {isOpenedCampaignModal && (
        <CampaignModal
          loading={loading}
          agentOptions={agents}
          phoneOptions={phones}
          initialValues={initialValues}
          onClose={handleClose}
          onSumbit={initialValues ? handleEdit : handleAdd}
        />
      )}
      <CampaignsListTemplate
        listResult={listResult}
        buttons={[
          {
            icon: 'refresh',
            label: 'Refresh',
            onClick: refresh,
            loading: reloading,
          },
          {
            icon: 'upload',
            label: 'Import',
            disabled: loading,
            action: 'manage_outreach_campaigns',
            onClick: () => handleStartImport(),
          },
          {
            icon: 'plus',
            type: 'primary',
            label: 'Add Campaign',
            disabled: initializing,
            onClick: handleStartAdd,
            action: 'manage_outreach_campaigns',
          },
        ]}
        columns={[
          {
            header: 'Name',
            sortName: 'name',
            cellClassName: 'text-small font-semibold',
            getValue: ({ id, name }) => (
              <CampaignName
                id={id}
                name={name}
                withPromptsLink
                withMonitoringLink
              />
            ),
          },
          {
            header: 'Status',
            sortName: 'status',
            cellClassName: 'max-w-[12rem]',
            getValue: ({ status }) => (
              <Slug
                color={getColor(status)}
                className="max-w-[10rem]"
                text={campaignStatusesMap[status] || status}
              />
            ),
          },
          {
            header: 'Description',
            sortName: 'description',
            cellClassName: 'text-small',
            getValue: ({ description }) => description,
          },
          {
            header: 'Contacts',
            sortName: 'contact_count',
            cellClassName: 'text-small',
            getValue: ({ contact_count }) => (
              <Slug
                text={contact_count || 0}
                className="min-w-[3rem] max-w-[7rem] w-fit"
              />
            ),
          },
          {
            header: 'Created',
            sortName: 'created_at',
            cellClassName: 'text-tiny',
            getValue: ({ created_at }) => formatDate(created_at),
          },
          {
            header: 'Actions',
            align: 'right',
            getValue: item => {
              return (
                <Buttons
                  visibleCount={2}
                  buttons={[
                    {
                      icon: 'eye',
                      label: 'View',
                      disabled: initializing,
                      tooltip: 'View campaign',
                      action: 'manage_outreach_campaigns',
                      onClick: () => handleView(item),
                    },
                    {
                      icon: 'edit',
                      type: 'primary',
                      label: 'Edit',
                      disabled: loading,
                      action: 'manage_outreach_campaigns',
                      onClick: () => handleStartEdit(item),
                    },
                    {
                      icon: 'copy',
                      label: 'Copy',
                      disabled: loading,
                      action: 'manage_outreach_campaigns',
                      onClick: () => handleStartCopy(item),
                    },
                    {
                      icon: 'document',
                      label: 'Secrets',
                      disabled: loading,
                      tooltip: 'Manage campaign secrets',
                      action: 'manage_outreach_campaigns',
                      onClick: () => handleStartSecrets(item),
                    },
                    {
                      icon: 'share',
                      label: 'Transfer',
                      disabled: loading,
                      tooltip: 'Transfer to another Organization',
                      action: 'manage_outreach_campaigns',
                      onClick: () => handleStartTransfer(item),
                    },
                    {
                      icon: 'download',
                      label: 'Export',
                      disabled: loading,
                      tooltip: 'Export campaign',
                      action: 'manage_outreach_campaigns',
                      onClick: () => handleStartExport(item),
                    },
                    {
                      icon: 'delete',
                      type: 'danger',
                      label: 'Delete',
                      action: 'manage_outreach_campaigns',
                      onClick: () => handleStartDelete(item),
                      disabled: loading || !!item.contact_count,
                      tooltip: item.contact_count ? deleteTooltip : undefined,
                    },
                  ]}
                />
              );
            },
          },
        ]}
      />
    </>
  );
};

export default Campaigns;
